import axios from "axios";
import {
  clearAuth,
  getStoredRefreshToken,
  getStoredToken,
  hasValidSession,
  markSessionExpired,
  refreshSession,
  touchSessionActivity,
} from "../services/authService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.nayrahenna.com";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let refreshPromise = null;

api.interceptors.request.use(
  async (config) => {
    if (config?.skipAuthRefresh) {
      return config;
    }

    const token = getStoredToken();
    const refreshToken = getStoredRefreshToken();

    if (!token) {
      return config;
    }

    if (!hasValidSession()) {
      if (!refreshToken) {
        clearAuth();
        markSessionExpired();

        if (window.location.pathname !== "/") {
          window.location.replace("/");
        }

        return config;
      }

      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshSession()
          .catch(() => {
            clearAuth();
            markSessionExpired();

            if (window.location.pathname !== "/") {
              window.location.replace("/");
            }

            throw new Error("Session expired.");
          })
          .finally(() => {
            isRefreshing = false;
            refreshPromise = null;
          });
      }

      await refreshPromise;
    }

    const latestToken = getStoredToken();

    if (latestToken) {
      config.headers.Authorization = `Bearer ${latestToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    if (!response?.config?.skipActivityTouch) {
      touchSessionActivity();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;
    const hadToken = Boolean(getStoredToken());
    const hasRefreshToken = Boolean(getStoredRefreshToken());

    if (
      (status === 401 || status === 403) &&
      hadToken &&
      hasRefreshToken &&
      !originalRequest?._retry &&
      !originalRequest?.skipAuthRefresh
    ) {
      originalRequest._retry = true;

      try {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshSession().finally(() => {
            isRefreshing = false;
            refreshPromise = null;
          });
        }

        await refreshPromise;

        const latestToken = getStoredToken();
        if (latestToken) {
          originalRequest.headers.Authorization = `Bearer ${latestToken}`;
        }

        return api(originalRequest);
      } catch {
        clearAuth();
        markSessionExpired();

        if (window.location.pathname !== "/") {
          window.location.replace("/");
        }
      }
    }

    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Unexpected request error.";

    return Promise.reject(new Error(message));
  }
);

export { API_BASE_URL };
export default api;